import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { TEAMS, ROLES } from "@/lib/crmConstants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Save, MapPin, Store as StoreIcon } from "lucide-react";

function TeamCheckboxes({ selected, onChange }) {
  const toggle = (k) => {
    if (selected.includes(k)) onChange(selected.filter((x) => x !== k));
    else onChange([...selected, k]);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {TEAMS.map((t) => (
        <label key={t.key} className="flex items-center gap-2 text-sm" data-testid={`team-check-${t.key}`}>
          <Checkbox checked={selected.includes(t.key)} onCheckedChange={() => toggle(t.key)} />
          <span>{t.label}{t.individual && <span className="text-[10px] ml-1 text-[#71717A]">(territory)</span>}</span>
        </label>
      ))}
    </div>
  );
}

function StatesPicker({ selected, onChange, states }) {
  if (!states.length) return <div className="text-xs text-[#71717A]">Loading states…</div>;
  const toggle = (s) => {
    if (selected.includes(s)) onChange(selected.filter((x) => x !== s));
    else onChange([...selected, s]);
  };
  return (
    <div className="max-h-48 overflow-auto border border-[#E5E5E5] rounded-md p-2 grid grid-cols-2 gap-1">
      {states.map((s) => (
        <label key={s} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#F4F4F5] px-1 py-0.5 rounded">
          <Checkbox checked={selected.includes(s)} onCheckedChange={() => toggle(s)} />
          <span>{s}</span>
        </label>
      ))}
    </div>
  );
}

const emptyForm = { email: "", password: "", name: "", role: "team_member", teams: [], assigned_states: [], is_active: true, store_id: "", salesperson_id: "" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  }, []);
  useEffect(() => {
    load();
    api.get("/meta/states").then((r) => setStates(r.data));
    api.get("/stores").then((r) => setStores(r.data)).catch(() => setStores([]));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    }
  };

  const saveEdit = async () => {
    try {
      const upd = { name: editing.name, role: editing.role, teams: editing.teams, assigned_states: editing.assigned_states };
      if (editing.newPassword) upd.password = editing.newPassword;
      if (editing.role === "sales_agent") {
        upd.store_id = editing.store_id || "";
        upd.salesperson_id = editing.salesperson_id || "";
      }
      if (typeof editing.is_active === "boolean") upd.is_active = editing.is_active;
      await api.patch(`/users/${editing.id}`, upd);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const showsTerritory = (u) => (u.teams || []).some((t) => t === "sales" || t === "service");

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Access control</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Users & Teams</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="add-user-button">
              <UserPlus className="w-4 h-4 mr-1" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create user</DialogTitle>
              <DialogDescription>Add a new team member, team lead, or admin. Sales / Service team members can be given territories that auto-route matching records.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Full name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Password</Label>
                <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role !== "admin" && form.role !== "sales_agent" && form.role !== "sales_incentive" && (
                <div>
                  <Label className="data-label mb-1.5 block">Teams</Label>
                  <TeamCheckboxes selected={form.teams} onChange={(v) => setForm({ ...form, teams: v })} />
                </div>
              )}
              {form.role === "sales_agent" && (
                <>
                  <div>
                    <Label className="data-label mb-1.5 block flex items-center gap-1">
                      <StoreIcon className="w-3 h-3" /> Store
                    </Label>
                    <Select value={form.store_id || "__none"} onValueChange={(v) => setForm({ ...form, store_id: v === "__none" ? "" : v })}>
                      <SelectTrigger data-testid="user-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— No store —</SelectItem>
                        {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {stores.length === 0 && (
                      <div className="text-[10px] text-[#DC2626] mt-1">
                        No stores yet. Add one from the Stores page first.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Unique Salesperson ID</Label>
                    <Input
                      required
                      value={form.salesperson_id}
                      onChange={(e) => setForm({ ...form, salesperson_id: e.target.value })}
                      placeholder="e.g. HEM-BLR-01-A03"
                      data-testid="user-salesperson-id-input"
                    />
                    <div className="text-[10px] text-[#71717A] mt-1">
                      This ID is entered on the product registration form to auto-credit the agent.
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
                  data-testid="user-active-checkbox"
                />
                <span className="text-sm">Active (uncheck to block login)</span>
              </div>
              {form.role === "team_member" && (form.teams.includes("sales") || form.teams.includes("service")) && (
                <div>
                  <Label className="data-label mb-1.5 block flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Assigned states (auto-routing)
                  </Label>
                  <StatesPicker selected={form.assigned_states} onChange={(v) => setForm({ ...form, assigned_states: v })} states={states} />
                  <div className="text-[10px] text-[#71717A] mt-1">
                    Records with these states will be auto-assigned to this user.
                    Change the mapping any time from the Territories page.
                  </div>
                </div>
              )}
              <Button type="submit" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="user-submit">
                Create user
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Teams / Store</th><th>States / Agent ID</th><th>Status</th><th></th></tr>
          </thead>
          <tbody data-testid="users-tbody">
            {users.map((u) => {
              const roleColor = u.role === "admin"
                ? { bg: "#EEF2FF", text: "#4338CA", border: "#C7D2FE" }
                : u.role === "team_lead"
                ? { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" }
                : u.role === "sales_agent"
                ? { bg: "#DCFCE7", text: "#166534", border: "#86EFAC" }
                : u.role === "sales_incentive"
                ? { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" }
                : { bg: "#F4F4F5", text: "#3F3F46", border: "#E4E4E7" };
              const storeName = stores.find((s) => s.id === u.store_id)?.name;
              return (
              <tr key={u.id} data-testid={`user-row-${u.email}`} style={u.is_active === false ? { opacity: 0.5 } : {}}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border font-mono"
                    style={{ backgroundColor: roleColor.bg, color: roleColor.text, borderColor: roleColor.border }}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="text-xs text-[#52525B]">
                  {u.role === "sales_agent"
                    ? (storeName || <span className="text-[#A1A1AA]">no store</span>)
                    : ((u.teams || []).join(", ") || "—")}
                </td>
                <td className="text-xs text-[#52525B] max-w-[240px]">
                  {u.role === "sales_agent"
                    ? (u.salesperson_id ? <span className="font-mono">{u.salesperson_id}</span> : <span className="text-[#A1A1AA]">no ID</span>)
                    : showsTerritory(u)
                      ? ((u.assigned_states || []).join(", ") || <span className="text-[#A1A1AA]">none</span>)
                      : "—"}
                </td>
                <td>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono ${u.is_active === false ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#DCFCE7] text-[#166534]"}`}>
                    {u.is_active === false ? "blocked" : "active"}
                  </span>
                </td>
                <td className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...u, newPassword: "", assigned_states: u.assigned_states || [] })} data-testid={`edit-user-${u.email}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`delete-user-${u.email}`}>
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </Button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update this user&apos;s name, role, teams, assigned states, or reset their password.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Role</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editing.role !== "admin" && editing.role !== "sales_agent" && editing.role !== "sales_incentive" && (
                <div>
                  <Label className="data-label mb-1.5 block">Teams</Label>
                  <TeamCheckboxes selected={editing.teams || []} onChange={(v) => setEditing({ ...editing, teams: v })} />
                </div>
              )}
              {editing.role === "sales_agent" && (
                <>
                  <div>
                    <Label className="data-label mb-1.5 block flex items-center gap-1">
                      <StoreIcon className="w-3 h-3" /> Store
                    </Label>
                    <Select value={editing.store_id || "__none"} onValueChange={(v) => setEditing({ ...editing, store_id: v === "__none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— No store —</SelectItem>
                        {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Unique Salesperson ID</Label>
                    <Input
                      value={editing.salesperson_id || ""}
                      onChange={(e) => setEditing({ ...editing, salesperson_id: e.target.value })}
                    />
                  </div>
                </>
              )}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editing.is_active !== false}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: !!v })}
                  data-testid="edit-user-active-checkbox"
                />
                <span className="text-sm">Active (uncheck to block login)</span>
              </div>
              {editing.role === "team_member" && ((editing.teams || []).includes("sales") || (editing.teams || []).includes("service")) && (
                <div>
                  <Label className="data-label mb-1.5 block flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Assigned states
                  </Label>
                  <StatesPicker selected={editing.assigned_states} onChange={(v) => setEditing({ ...editing, assigned_states: v })} states={states} />
                </div>
              )}
              <div>
                <Label className="data-label mb-1.5 block">Reset password (optional)</Label>
                <Input type="password" value={editing.newPassword} onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })} placeholder="Leave blank to keep" />
              </div>
              <Button onClick={saveEdit} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="save-user-edit">
                <Save className="w-4 h-4 mr-1" /> Save changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
